import { useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Crown, Shield, User as UserIcon, Download, Trash2, ShieldAlert } from "lucide-react";

const ROLE_LABEL = {
  owner: { label: "Dueño de la cuenta", icon: Crown, color: "bg-amber-100 text-amber-800" },
  administrador: { label: "Administrador", icon: Shield, color: "bg-violet-100 text-violet-800" },
  supervisor: { label: "Supervisor", icon: Shield, color: "bg-blue-100 text-blue-800" },
  usuario: { label: "Usuario", icon: UserIcon, color: "bg-slate-100 text-slate-700" },
};

export default function ProfilePage() {
  const { user, login, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    try {
      const { data } = await api.get("/auth/export-my-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cotflow-mis-datos-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Datos exportados");
    } catch { toast.error("Error al exportar datos"); }
  };

  const handleDelete = async () => {
    const confirmText = user?.role === "owner"
      ? "Vas a eliminar tu cuenta y toda la información de tu empresa. Esta acción es irreversible tras 30 días."
      : "Vas a eliminar tu cuenta. Esta acción es irreversible tras 30 días.";
    if (!window.confirm(`${confirmText}\n\nEscribe ELIMINAR para confirmar.`)) return;
    const answer = window.prompt('Escribe la palabra ELIMINAR en mayúsculas para confirmar:');
    if (answer !== "ELIMINAR") return toast.error("Cancelado");
    setDeleting(true);
    try {
      await api.delete("/auth/delete-my-account");
      toast.success("Cuenta marcada para eliminación. Cerrando sesión.");
      setTimeout(() => logout(), 1500);
    } catch { toast.error("Error al eliminar cuenta"); }
    setDeleting(false);
  };

  if (!user) return null;
  const meta = ROLE_LABEL[user.role] || ROLE_LABEL.usuario;
  const RoleIcon = meta.icon;

  const saveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("El nombre no puede estar vacío");
    setSavingName(true);
    try {
      const { data } = await api.put("/auth/profile", { name });
      login(localStorage.getItem("qf_token"), data);
      toast.success("Perfil actualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setSavingName(false);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) return toast.error("La nueva contraseña debe tener al menos 6 caracteres");
    if (pwForm.new_password !== pwForm.confirm) return toast.error("Las contraseñas no coinciden");
    if (!user.must_change_password && !pwForm.current_password) return toast.error("Ingresa tu contraseña actual");
    setSavingPw(true);
    try {
      await api.put("/auth/change-password", {
        current_password: pwForm.current_password || null,
        new_password: pwForm.new_password,
      });
      // Refrescar user con must_change_password=false
      const { data } = await api.get("/auth/me");
      login(localStorage.getItem("qf_token"), data);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      toast.success("Contraseña actualizada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setSavingPw(false);
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900">Mi perfil</h1>
        <p className="text-slate-500 text-sm mt-1">Actualiza tus datos y contraseña.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Rol</div>
            <Badge className={`${meta.color} border-0 mt-1`}>
              <RoleIcon size={12} className="mr-1" /> {meta.label}
            </Badge>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-slate-500">Email</div>
            <div className="text-sm font-medium text-slate-900">{user.email}</div>
          </div>
        </div>
        <form onSubmit={saveName} className="space-y-3 pt-3 border-t border-slate-100">
          <div>
            <Label>Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <Button type="submit" disabled={savingName} className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">
            {savingName ? "Guardando…" : "Guardar cambios"}
          </Button>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Cambiar contraseña</h2>
        <p className="text-slate-500 text-sm mb-4">
          {user.must_change_password
            ? "Debes cambiar tu contraseña temporal antes de seguir usando la plataforma."
            : "Por seguridad, te pediremos tu contraseña actual."}
        </p>
        <form onSubmit={savePassword} className="space-y-3">
          {!user.must_change_password && (
            <div>
              <Label>Contraseña actual</Label>
              <Input
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                data-testid="current-password-input"
              />
            </div>
          )}
          <div>
            <Label>Nueva contraseña</Label>
            <Input
              type="password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
              data-testid="new-password-input"
            />
          </div>
          <div>
            <Label>Confirmar nueva contraseña</Label>
            <Input
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              data-testid="confirm-password-input"
            />
          </div>
          <Button type="submit" disabled={savingPw} className="bg-blue-600 hover:bg-blue-700" data-testid="save-password-btn">
            {savingPw ? "Guardando…" : "Actualizar contraseña"}
          </Button>
        </form>
      </div>

      {/* Datos y privacidad (Ley 21.719) */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Mis datos y privacidad</h2>
        <p className="text-slate-500 text-sm mb-4">
          Descarga toda la información asociada a tu cuenta o solicita su eliminación definitiva
          (derecho de acceso, portabilidad y cancelación · Ley 21.719).
        </p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handleExport} data-testid="export-data-btn">
            <Download size={14} className="mr-1.5" /> Exportar mis datos (JSON)
          </Button>
          <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            onClick={handleDelete} disabled={deleting} data-testid="delete-account-btn">
            <Trash2 size={14} className="mr-1.5" /> {deleting ? "Procesando…" : "Eliminar mi cuenta"}
          </Button>
        </div>
        <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
          <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            Al eliminar tu cuenta, será deshabilitada de inmediato y purgada definitivamente en un plazo
            de 30 días. Si eres el dueño de la cuenta, esta acción afecta también a toda tu empresa.
            Consulta la <a href="/legal/privacy" target="_blank" rel="noreferrer" className="underline font-medium">Política de Privacidad</a> para más detalles.
          </span>
        </div>
      </div>
    </div>
  );
}
